import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
interface FaqItem {
  questionKey: string;
  answer: string | React.ReactNode;
  answerKey?: string;
}
const faqs: FaqItem[] = [{
  questionKey: 'faq.q1',
  answer: <>
        We deliver throughout King and Pierce counties — Bellevue, Kirkland,
        Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma,
        Puyallup, Bonney Lake, and beyond.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-783">
          View our full delivery zone map →
        </Link>
      </>
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2',
  answer: ''
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3',
  answer: ''
}, {
  questionKey: 'faq.q4',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-784">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-785">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5',
  answer: ''
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6',
  answer: ''
}, {
  questionKey: 'faq.q7',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-619-3742" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-786">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-787">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
}];
export function FAQ() {
  const t = useT();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lastClosedIndex, setLastClosedIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    if (openIndex === index) {
      // Closing this question — mark it as last closed
      setLastClosedIndex(index);
      setOpenIndex(null);
    } else {
      // Opening a new question — clear last closed
      setLastClosedIndex(null);
      setOpenIndex(index);
    }
  };
  return <section className="py-16 sm:py-24 dark:bg-[#3d3a3a] transition-colors duration-300 scroll-mt-32 border-t border-stone-400 dark:border-transparent bg-[#f4f7f2]" id="faq" data-id="element-788">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-12 sm:mb-20 text-center" data-id="element-789">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-790">
          <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-791">
            {t('faq.title')}
          </h2>
          <p className="text-lg sm:text-xl text-stone-700 dark:text-stone-200 max-w-2xl mx-auto" data-id="element-792">
            {t('faq.subtitle')}
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-793">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        const answerContent = faq.answerKey ? t(faq.answerKey) : faq.answer;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#166534]' : 'bg-transparent'}`} data-id="element-794">
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-795">
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-6 py-8 sm:py-12 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-796">
                  <span className={`font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedIndex === index ? 'text-[#059F40] dark:text-[#059F40] group-hover:text-[#048036] dark:group-hover:text-[#048036]' : 'text-stone-900 dark:text-white group-hover:text-[#059F40] dark:group-hover:text-[#059F40]'}`} data-id="element-797">
                    {t(faq.questionKey)}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-1 sm:mt-2 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-798">
                    <Plus className="w-8 h-8 sm:w-12 sm:h-12 stroke-[1]" data-id="element-799" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-800">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${index}`} data-id="element-801">
                      <p className="pb-12 sm:pb-16 text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-4xl font-medium" data-id="element-802">
                        {answerContent}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-803">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-804">
          <MessageCircle className="w-5 h-5 text-[#059F40] shrink-0" data-id="element-805" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-806">
            {t('faq.stillQuestions')}
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-807">
              {t('faq.shootText')}
            </span>
          </p>
          <a href="sms:206-619-3742" className="inline-flex items-center justify-center px-6 py-2.5 bg-[#059F40] hover:bg-[#048B3A] text-white rounded-full font-bold text-sm transition-colors" data-id="element-808">
            {t('faq.textUs')}
          </a>
        </div>
      </div>
    </section>;
}